/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, CircularProgress, Stack, TextField } from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";

import { useWindowDimensions } from "../../utils/Hooks";
import {
    Nullable,
    StorageLocation,
    StorageLocationDetailed,
    TechwiseOffice,
    TechwiseOfficeDetailed,
} from "../../utils/Interfaces";
import { updateRestockingTasks } from "./materialsRestockingFunctions";
import { LocationType } from "../../utils/Constants";
import DataTable from "../../components/table/DataTable";
import { getInventoryCompilation } from "../../utils/Functions";
import { GridRenderCellParams } from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";
import { selectExistingTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesSlice";
import Autocomplete from "@mui/material/Autocomplete";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import TransferRow from "./TransferRow";
import { techwiseOfficeDetails } from "../materials-techwise-office/techwiseOfficeFunctions";
import InfoModal from "../../components/modal/InfoModal";

const TechnicianRestocking = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { height } = useWindowDimensions();

    const techwiseOffices: TechwiseOffice[] = useAppSelector(selectExistingTechwiseOffices);
    const [techwiseOffice, setTechwiseOffice] = useState<Nullable<TechwiseOffice>>(null);
    const [officeDetails, setOfficeDetails] = useState<Nullable<TechwiseOfficeDetailed>>(null);
    const [storageLocationsWithTasks, setStorageLocationsWithTasks] = useState<StorageLocationDetailed[]>([]);

    const [transferFailureModal, setTransferFailureModal] = useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (techwiseOffices.length > 0) {
            setTechwiseOffice(techwiseOffices[0]);
        }
    }, [techwiseOffices]);

    useEffect(() => {
        if (techwiseOffice !== null) {
            updateRestockingTasks(
                techwiseOffice.id,
                LocationType.TECHNICIAN_VAN,
                dispatch,
                setStorageLocationsWithTasks,
                setLoading
            );
            techwiseOfficeDetails(techwiseOffice.id, dispatch, setOfficeDetails);
        }
    }, [techwiseOffice]);

    let columns: any[] = [
        {
            field: "storage_location_name",
            width: 165,
            headerName: "Location",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.storage_location.name;
            },
        },
        {
            field: "material_part_no",
            width: 165,
            headerName: "Part #",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.material.part_number;
            },
        },
        {
            field: "material_name",
            width: 300,
            headerName: "Part/Material Name",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.material.name;
            },
        },
        {
            field: "target",
            width: 100,
            headerName: "Target",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.target;
            },
        },
        {
            field: "amount",
            width: 100,
            headerName: "Current Amt.",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.amount;
            },
        },
        {
            field: "operation",
            width: 180,
            headerName: "Operation",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.target - params.row.amount;
            },
            renderCell: (params: GridRenderCellParams) => {
                let delta: number = params.row.target - params.row.amount;

                return (
                    <Chip
                        color={delta > 0 ? "warning" : "success"}
                        label={delta > 0 ? `RESTOCK ${Math.abs(delta)}` : `EXCESS ${Math.abs(delta)}`}
                        style={{ fontWeight: "bold" }}
                    />
                );
            },
        },
        {
            field: "transfer_row",
            width: 500,
            headerName: "Commit Transfer",
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.target - params.row.amount;
            },
            renderCell: (params: GridRenderCellParams) => {
                let delta: number = params.row.target - params.row.amount;

                return (
                    <TransferRow
                        amount={delta}
                        permanentLocations={(officeDetails?.locations ?? []).filter(
                            (location: StorageLocation) => location.location_type === "Permanent"
                        )}
                        sourceLocation={params.row.storage_location}
                        material={params.row.material}
                        onTransferSuccess={() =>
                            updateRestockingTasks(
                                techwiseOffice?.id ?? 0,
                                LocationType.TECHNICIAN_VAN,
                                dispatch,
                                setStorageLocationsWithTasks,
                                setLoading
                            )
                        }
                        onTransferFailure={() => setTransferFailureModal(true)}
                    />
                );
            },
        },
    ];

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Materials", route: "/?section=materials" },
                    { text: "Technician Restocking", route: "/materials/restocking/technician" },
                ]}
            />

            <InfoModal
                title="Transfer Failed"
                description="The transfer could not be completed. There is not enough material in the source location."
                open={transferFailureModal}
                handleClose={() => setTransferFailureModal(false)}
            />

            <Box sx={{ p: 1, mt: 2 }}>
                <Autocomplete
                    disablePortal
                    onChange={(_event, value) => {
                        setTechwiseOffice(value ? value : techwiseOffices.length > 0 ? techwiseOffices[0] : null);
                    }}
                    id="techwiseOffices"
                    value={techwiseOffice}
                    getOptionLabel={(option) => option.name}
                    getOptionKey={(option: TechwiseOffice) => option.id}
                    options={techwiseOffices}
                    renderInput={(params) => <TextField required {...params} label="Select Techwise Office" />}
                />
            </Box>

            {loading && (
                <Stack direction="row" justifyContent="center">
                    <CircularProgress size={40} sx={{ mt: 15, mb: 5 }} />
                </Stack>
            )}

            {!loading && (
                <Card sx={{ mt: 1 }}>
                    <CardContent>
                        <DataTable
                            tableName="TECHNICIAN_RESTOCKING"
                            data={getInventoryCompilation(storageLocationsWithTasks)}
                            columns={columns}
                            height={height - 280}
                            allowRowSelected={false}
                        />
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default TechnicianRestocking;
